import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Pause, Play, Volume2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fadeAudio } from "@/hooks/useMiaVoice";

const usePreview = () => {
  const audio = useRef(null);
  const urls = useRef({});
  const [state, setState] = useState({ id: null, loading: false });
  useEffect(() => () => { audio.current?.pause(); Object.values(urls.current).forEach((u) => URL.revokeObjectURL(u)); }, []);
  const toggle = async (id) => {
    audio.current?.pause();
    if (state.id === id && !state.loading) { setState({ id: null, loading: false }); return; }
    setState({ id, loading: !urls.current[id] });
    try {
      if (!urls.current[id]) {
        const { data } = await api.get(`/ai/voice/preview/${id}`, { responseType: "blob" });
        urls.current[id] = URL.createObjectURL(data);
      }
      const a = new Audio(urls.current[id]);
      audio.current = a;
      a.volume = 0;
      a.onended = () => setState((s) => (s.id === id ? { id: null, loading: false } : s));
      await a.play();
      fadeAudio(a, 1);
      setState({ id, loading: false });
    } catch (e) { toast.error(errMsg(e, "Couldn't play the preview")); setState({ id: null, loading: false }); }
  };
  return { state, toggle };
};

const VoiceOption = ({ o, chosen, preview, onPick }) => {
  const on = preview.state.id === o.id;
  return (
    <div className={`mm-soft p-3 flex items-center gap-3 transition-colors ${chosen ? "ring-2 ring-mm-primary" : ""}`} data-testid={`mia-voice-option-${o.id}`}>
      <button onClick={() => preview.toggle(o.id)} className="w-10 h-10 shrink-0 rounded-full mm-gradient text-white flex items-center justify-center" aria-label={on ? `Stop ${o.id} preview` : `Preview ${o.id}`} data-testid={`mia-voice-preview-${o.id}`}>
        {on && preview.state.loading ? <Loader2 size={16} className="animate-spin" /> : on ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <button onClick={() => onPick(o.id)} className="flex-1 text-left min-w-0" data-testid={`mia-voice-pick-${o.id}`}>
        <div className="font-semibold capitalize">{o.id}{o.id === "sage" && <span className="text-xs font-normal text-mm-muted"> · default</span>}</div>
        <div className="text-xs text-mm-muted">{o.label}</div>
      </button>
      {chosen && <Check size={18} className="text-mm-primary shrink-0" data-testid={`mia-voice-chosen-${o.id}`} />}
    </div>
  );
};

// Choose which HD voice Mia speaks with, with a short preview of each
export const MiaVoiceCard = () => {
  const [d, setD] = useState(null);
  const preview = usePreview();
  useEffect(() => { api.get("/ai/voice").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const pick = async (voice) => {
    if (voice === d.voice) return;
    const prev = d.voice;
    setD({ ...d, voice });
    try { await api.put("/ai/voice", { voice }); toast.success(`Mia will now speak with the ${voice} voice`); }
    catch (e) { setD((x) => ({ ...x, voice: prev })); toast.error(errMsg(e)); }
  };
  return (
    <div className="mm-card p-5" data-testid="mia-voice-card">
      <div className="flex items-center gap-2 font-semibold"><Volume2 size={18} className="text-mm-primary" />Choose how Mia sounds</div>
      <p className="text-sm text-mm-muted mt-1">Tap play to hear each voice, then pick the one that feels most calming to you.</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {d.options.map((o) => <VoiceOption key={o.id} o={o} chosen={d.voice === o.id} preview={preview} onPick={pick} />)}
      </div>
    </div>
  );
};
